import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ListingsCard from "../Components/ListingsCard";
import { hiddenCategories } from "../Constants/hiddenCategories";
import { getPreferredListings, getListings } from "../Services/listingsApi";
import { getCities,getCategoriesByCityId } from "../Services/citiesApi";
import { getCategory } from "../Services/CategoryApi";
import { getCurrentUser } from "../Services/usersApi";
import CITYIMAGE from "../assets/City.png";
import CITYDEFAULTIMAGE from "../assets/CityDefault.png";
import TerminalDefaultBackground from '../assets/Terminal-Background.png';
import ShopingCart from "../assets/dummyimage.jpg";
import { fetcTerminalData } from "../Services/terminal";
import ListingPopUpPreview from "../Components/ListingPopUpPreview";
import ListingsCardTerminal from "../Components/ListCardTerminal";
import ServiceViewModal from "../Components/ServicesView";
import { QrCodes } from "./HorizontalCompo";
import { ServicesHorizontal } from "./HorizontalCompo";
import { ArrowDown } from "lucide-react";
import useScrollableDown from "../hook/useScrollableDown";
import {
  isSolidColorImage,
  prioritizeItemsByKey,
  splitArr,
} from "../Services/helper";
import { set } from "lodash-es";
import { categoryByName } from "../Constants/categories";
import { VerticalQrCodes, VerticalServices } from "./VerticalCompo";
import { useInactivity } from "../hook/useInActivity";
import { TERMINAL_INACTIVITY_TIME } from "../Constants/constant";
import DisplayAdvertisement from "../Components/DisplayAdvertisement";
import { useActiveAds } from "../hook/useActiveAds";
import useAutoThemeLock from "../hook/useAutoThemeLock";

function TerminalViewSkeleton() {
  return (
    <div className="animate-pulse relative">
      {/* Background */}
      <div className="absolute inset-0 -z-10 w-full h-full bg-gray-100" />

      <div className="flex flex-col lg:flex-row gap-4">
        {/* Categories/Listing Section */}
        <div className="mt-12 w-full lg:w-[46%] flex flex-col justify-center items-center mt-24 space-y-8">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="w-full space-y-4">
              <div className="h-8 w-48 bg-gray-300 rounded mx-auto"></div>
              <div className="flex flex-wrap justify-center gap-4 w-full">
                {Array.from({ length: 3 }).map((_, j) => (
                  <div
                    key={j}
                    className="w-[20rem] h-[15rem] bg-gray-200 rounded-lg"
                  ></div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Vertical separator */}
        <div className="hidden lg:flex h-[100vh] flex-col justify-center">
          <div className="w-2 h-[90vh] bg-gray-300"></div>
        </div>

        {/* Services + QR Codes */}
        <div className="w-full lg:w-[46%] mb-6 space-y-12">
          {/* Services Label */}
          <div className="w-full flex justify-center items-center mt-24 mb-4">
            <div className="h-10 w-48 bg-gray-300 rounded"></div>
          </div>

          {/* Services Grid */}
          <div className="flex flex-wrap justify-center gap-4 w-full px-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="w-[25rem] h-[20rem] bg-gray-200 rounded-lg"
              ></div>
            ))}
          </div>

          {/* QR Codes Label */}
          <div className="w-full flex justify-center items-center mt-24 mb-4">
            <div className="h-10 w-48 bg-gray-300 rounded"></div>
          </div>

          {/* QR Codes Grid */}
          <div className="flex flex-wrap justify-center gap-4 w-full px-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="flex flex-col justify-end border rounded-lg bg-gray-50 w-[25rem] h-[20rem] p-4 space-y-2"
              >
                <div className="h-8 w-32 bg-gray-300 rounded"></div>
                <div className="h-32 w-full bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const ScrollerStruct = ({ children, terminalData, className }) => {
  const scrollListingRef = useRef(null);

  const scrollListingLeft = () => {
    if (scrollListingRef.current) {
      scrollListingRef.current.scrollBy({ left: -300, behavior: "smooth" });
    }
  };

  const scrollListingRight = () => {
    if (scrollListingRef.current) {
      scrollListingRef.current.scrollBy({ left: 300, behavior: "smooth" });
    }
  };

  return (
    <>
      <button
        onClick={scrollListingLeft}
        className="absolute left-2 top-1/2  z-20 -translate-y-1/2 bg-gray-800 text-white rounded-full p-2 hover:bg-gray-700"
      >
        ❮
      </button>
      <div ref={scrollListingRef} className={className}>
        {children}
      </div>
      <button
        onClick={scrollListingRight}
        className="absolute right-2 top-1/2 z-20  -translate-y-1/2 bg-gray-800 text-white rounded-full p-2 hover:bg-gray-700"
      >
        ❯
      </button>
    </>
  );
};

const ListingCompo = ({
  terminalData,
  name,
  listings,
  terminalView,
  setListingPopup,
  isWrapLayout = false,
  isOnlyCategory = false,
  isExpanded,
  handleToggleExpanded,
  sliptIntoTwo = false,
  index,
}) => {
  const { t } = useTranslation();
  // Fallback state for when props are not provided
  const [localIsExpanded, setLocalIsExpanded] = useState(false);

  // Use props if provided, otherwise use local state
  const currentIsExpanded =
    isExpanded !== undefined ? isExpanded : localIsExpanded;
  const currentToggleExpanded =
    handleToggleExpanded || (() => setLocalIsExpanded(!localIsExpanded));

  // Common function to determine if expand/collapse controls should be shown
  const shouldShowExpandControls = () => {
    return isWrapLayout && listings?.length > 6;
  };

  // For wrap layout, determine how many items to show
  const itemsToShow =
    isWrapLayout && !currentIsExpanded ? 6 : listings?.length || 0;
  const displayedListings = isWrapLayout
    ? listings?.slice(0, itemsToShow)
    : listings;
  const hasMoreItems = shouldShowExpandControls();

  const [firstHalfCategories, secondHalfCategories] = useMemo(() => {
    const arr = listings || [];
    if (arr?.length > 6) {
      const half = Math.ceil(arr.length / 2);
      return [arr.slice(0, half), arr.slice(half)];
    } else {
      return [arr]; // Return as a single group if 6 or less
    }
  }, [listings]);

  if (isWrapLayout && isOnlyCategory) {
    return (
      <div className="px-5 py-0 mt-0 mb-0 flex flex-col w-full">
        <p
          className="px-4 mb-4 text-gray-900 font-bold text-2xl 3xl:text-[4rem] title-font text-start font-sans font-bold flex justify-between items-center"
          style={{
            fontFamily: "'Space Grotesk', Helvetica, Arial, Lucida, sans-serif",
          }}
        >
          {name ? t(name) : ""}
        </p>
        {/* Wrap Layout */}
        <div className="flex flex-wrap justify-center gap-4 px-4">
          {displayedListings?.map((listing, index) => (
            <div key={index} className="flex-shrink-0">
              <ListingsCardTerminal
                listing={listing}
                terminalView={terminalView}
                isPopUp={true}
                setListingPopup={setListingPopup}
                orientation={terminalData?.orientation}
                className={
                  terminalData?.orientation === "horizontal" && "2xl:h-[17rem]"
                }
                type={"B"}
              />
            </div>
          ))}
        </div>

        {/* Load More / Show Less Button */}
        {hasMoreItems && (
          <div className="flex justify-center mt-3">
            <button
              onClick={currentToggleExpanded}
              className="bg-gray-800 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors duration-200 font-medium"
            >
              {currentIsExpanded
                ? "Show Less"
                : `Load More (${listings.length - itemsToShow} more)`}
            </button>
          </div>
        )}
      </div>
    );
  }

  // FOR ONLY ONE CATEGORY
  if (terminalData.categories.length === 1) {
    return (
      <div className="px-5  py-1 mt-0 mb-0 flex flex-col">
        <p
          className="dark:text-white px-4 mb-4 text-gray-900 font-bold text-2xl 3xl:text-[4rem]  title-font text-start font-sans font-bold flex justify-between items-center"
          style={{
            fontFamily: "'Space Grotesk', Helvetica, Arial, Lucida, sans-serif",
          }}
        >
          {name ? t(name) : ""}
        </p>
        {!!firstHalfCategories?.length && (
          <div className="relative w-full h-fit mb-8">
            <ScrollerStruct
              terminalData={terminalData}
              className="relative place-items-center h-full mb-0 flex gap-4 overflow-x-auto whitespace-nowrap scrollbar-hide px-4"
            >
              {firstHalfCategories?.map((listing, index) => (
                <div className="flex-shrink-0 snap-start gap-2" key={index}>
                  <ListingsCardTerminal
                    listing={listing}
                    terminalView={terminalView}
                    isPopUp={true}
                    setListingPopup={setListingPopup}
                    orientation={terminalData?.orientation}
                    type="B"
                  />
                </div>
              ))}
            </ScrollerStruct>
          </div>
        )}
        {!!secondHalfCategories?.length && (
          <div className="relative w-full h-fit">
            <ScrollerStruct
              terminalData={terminalData}
              className="relative place-items-center h-full mb-0 flex gap-4 overflow-x-auto whitespace-nowrap scrollbar-hide px-4"
            >
              {secondHalfCategories?.map((listing, index) => (
                <div className="flex-shrink-0 snap-start gap-2" key={index}>
                  <ListingsCardTerminal
                    listing={listing}
                    terminalView={terminalView}
                    isPopUp={true}
                    setListingPopup={setListingPopup}
                    orientation={terminalData?.orientation}
                    type="B"
                  />
                </div>
              ))}
            </ScrollerStruct>
          </div>
        )}
      </div>
    );
  }

  // Original scroll layout
  return (
    <div className="px-5 py-1 mt-0 mb-8 flex flex-col">
      <p
        className={`
          ${index && "mt-8"}
          dark:text-white
          px-4 mb-8 text-gray-900 font-bold text-2xl 3xl:text-[4rem]  title-font text-start font-sans font-bold flex justify-between items-center
          `}
        style={{
          fontFamily: "'Space Grotesk', Helvetica, Arial, Lucida, sans-serif",
        }}
      >
        {name ? t(name) : ""}
      </p>
      <div className="relative w-full h-fit">
        <ScrollerStruct
          terminalData={terminalData}
          className="relative place-items-center h-full mb-0 flex gap-4 overflow-x-auto whitespace-nowrap scrollbar-hide px-4"
        >
          {listings?.map((listing, index) => (
            <div className="flex-shrink-0 snap-start gap-2" key={index}>
              <ListingsCardTerminal
                listing={listing}
                terminalView={terminalView}
                isPopUp={true}
                setListingPopup={setListingPopup}
                orientation={terminalData?.orientation}
                className={
                  terminalData?.orientation === "horizontal" &&
                  terminalData?.categories?.length < 2 &&
                  "2xl:h-[17rem]"
                }
                type="B"
              />
            </div>
          ))}
        </ScrollerStruct>
      </div>
    </div>
  );
};

const HomePageTempleteB = () => {
  const { t } = useTranslation();
  const [cityId, setCityId] = useState([]);
  const [categoryId, setCategoryId] = useState();
  const [cities, setCities] = useState([]);
  const [listings, setListings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showPopup, setShowPopup] = useState(false);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [terminalView, setTerminalView] = useState(false);
  const [isGridView, setIsGridView] = useState(true);
  const queryParams = new URLSearchParams(location.search);
  const [terminalData, setTerminalData] = useState(null);
  const terminalViewParam = queryParams.get("terminalView");
  const [listingpopup, setListingPopup] = useState(null);
  const [leftSideCategoryList, setLeftSideCategoryList] = useState([]);
  const [rightSideCategoryList, setRightSideCategoryList] = useState([]);
  const[poiCategories,setPoiCategories]=useState([])
  const [isMap,setIsMap]=useState(false)

  const [isInActivity, resetInactivity] = useInactivity(TERMINAL_INACTIVITY_TIME);
  useAutoThemeLock();
  const terminalCityId=terminalData?.cityId

  const isTerminalScreenEnvEnabled =
    process.env.REACT_APP_ENABLE_TERMINALSCREEN === "True";

  const isTerminalScreenEnabled =
    isTerminalScreenEnvEnabled && terminalViewParam === "true";

  const toggleView = () => {
    setIsGridView((prev) => !prev);
  };

  const navigate = useNavigate();
  const navigateTo = (path) => {
    if (path) {
      navigate(path);
    }
  };

  const getCategoryNameById = (id) => {
    const catObj = categories.find((c) => c.id === id);
    return catObj ? catObj.name : "";
  };

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
    return null;
  };

  const isScrollableB = useScrollableDown();

  useEffect(() => {
    const checkPrivacyPolicy = () => {
      const cookieVal = getCookie("privacyPolicyAcceptedGlobally");

      try {
        const parsed = JSON.parse(decodeURIComponent(cookieVal));
        if (parsed?.accepted === true) {
          setShowPopup(false);
        } else {
          setShowPopup(true);
        }
      } catch (err) {
        console.error("Invalid cookie format:", err);
        setShowPopup(true);
      }
    };

    checkPrivacyPolicy();
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    setTerminalView(isTerminalScreenEnabled);

    getCities()
      .then((citiesResponse) => {
        if (Array.isArray(citiesResponse?.data?.data)) {
          setCities(citiesResponse.data.data);
        } else {
          setCities([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching cities:", error);
        setCities([]);
      });

    const cityParam = parseInt(queryParams.get("cityId"));
    if (cityParam) {
      setCityId(cityParam);
    }
    const catParam = parseInt(queryParams.get("categoryId"));
    if (catParam) {
      setCategoryId(catParam);
    }

    getCategory()
      .then((response) => {
        const allCats = response?.data?.data || [];
        const filteredCats = allCats.filter(
          (cat) => !hiddenCategories.includes(cat.id)
        );
        setCategories(filteredCats);
      })
      .catch((err) => {
        console.error("Error fetching categories:", err);
        setCategories([]);
      });

    document.title = process.env.REACT_APP_REGION_NAME;
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    if (parseInt(categoryId)) {
      urlParams.set("categoryId", categoryId);
    } else {
      urlParams.delete("categoryId");
    }

    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    window.history.pushState({}, "", newUrl);

    fetchData();
  }, [cityId, categoryId, searchQuery]);

  const fetchData = async (locationParams = {}) => {
    try {
      setIsLoading(true);

      const accessToken =
        window.localStorage.getItem("accessToken") ||
        window.sessionStorage.getItem("accessToken");

      let userId =
        window.localStorage.getItem("userId") ||
        window.sessionStorage.getItem("userId");

      if (!userId && accessToken) {
        try {
          const res = await getCurrentUser();
          userId = res?.data?.id;
          if (userId) {
            localStorage.setItem("userId", userId);
          }
        } catch (error) {
          console.warn("User fetch failed, using public listings instead.");
        }
      }
    } catch (error) {
      console.error("Error fetching listings:", error);
      setListings([]);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  const clearSearchResults = () => {
    setListings([]);
    setSearchQuery("");
  };

  const getTheListings = (newCategoryId, event) => {
    event.preventDefault();
    setCategoryId(newCategoryId);
    clearSearchResults();
  };

  const handleSearch = (searchTerm) => {
    setSearchQuery(searchTerm);
  };

  const getCategoryImage = (catId) => {
    const catObj = categories.find((c) => c.id === catId);
    return catObj?.metadata?.logo || CITYIMAGE;
  };

  const { terminalId } = useParams();
  useEffect(() => {
    fetcTerminalData(terminalId).then((res) => {
      // NOTE: CATEGORIES PRIORITY ARE BEING UPDATED.
      if (res.data.data?.categories?.length) {
        const categories = prioritizeItemsByKey(res.data.data?.categories, [
          categoryByName.News,
          categoryByName.Events,
        ]);
        res.data.data.categories = categories;
      }
      setTerminalData(res.data?.data);
      setAdvertisements(res.data?.data?.advertisements || []);
    });
  }, [terminalId]);
  const [advertisements, setAdvertisements] = useState([]);
  const activeAds = useActiveAds(advertisements);

  const [selectedService, setSelectedService] = useState("");

  // State to control arrow icons visibility
  const [isExpanded, setIsExpanded] = useState(false);

  // Function to handle load more/show less clicks
  const handleToggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  useEffect(() => {
    if (terminalData?.header_url) {
      isSolidColorImage(terminalData?.header_url).then((res) => {
        if (res?.color) {
          const overlayEl = document.getElementById("header-image-overlay");
          if (overlayEl) {
            overlayEl.classList.remove("bg-gray-700");
            overlayEl.classList.remove("bg-opacity-75");
            if (res?.light) {
              overlayEl.classList.remove("text-white");
            }
          }
        }
      });
    }
  }, [terminalData]);

  useEffect(() => {
    if (terminalData?.categories?.length) {
      let a = 0;
      if (!!terminalData?.services?.length) {
        a++;
      }
      if (!!terminalData?.qrCodes?.length) {
        a++;
      }
      if (!a) {
        setLeftSideCategoryList(terminalData.categories);
      } else {
        const r = splitArr(terminalData.categories, a);
        setLeftSideCategoryList(r[0]);
        setRightSideCategoryList(r[1]);
      }
    }
  }, [terminalData]);
    const getCategories=useCallback(async(terminalCityId)=>{
      try{
      const {data}=await getCategoriesByCityId(terminalCityId)
      if(data?.status){
        setPoiCategories(data?.data)
      }
    }
    catch(e){
      console.error(e)
    }

    },[])

    useEffect(() => {
        if (terminalCityId) {
          // getPoiForTerminal(cityId);
          getCategories(terminalCityId)
        }
      }, [terminalCityId, getCategories]);
  return (
    <section
      className="text-gray-900 body-font relative"
      onClick={() => setListingPopup(null)}
    >
      <div>
        {listingpopup && listings && (
          <ListingPopUpPreview
            listing={listingpopup}
            setListingPopup={setListingPopup}
          />
        )}
        <div className="flex flex-col relative p-0">
          <div
            style={{
              backgroundImage: `url(${
                terminalData?.logo_url || TerminalDefaultBackground
              })`,
            }}
            className="absolute z-20 left-4 top-4 lg:top-2 xl:top-4 h-10 w-10 sm:h-28 sm:w-28 lg:h-28 lg:w-28 xl:h-32 xl:w-32 2xl:w-30 2xl:h-30 3xl:w-40 3xl-w-40 rounded bg-center bg-no-repeat bg-contain"
            aria-label="Logo"
          />
          <div className="w-full">
            <div className="h-[24vh] lg:h-full overflow-hidden px-0 py-0 relative">
              <div className="relative md:h-[14vh] 2xl:h-[18vh] w-full aspect-[16/9] overflow-hidden">
                <img
                  alt="ecommerce"
                  className="object-cover object-center h-full w-full"
                  src={terminalData?.header_url || TerminalDefaultBackground}
                  loading="lazy"
                />
                <div
                  id="header-image-overlay"
                  className="absolute inset-0 flex flex-col items-center justify-center bg-gray-700 bg-opacity-75 text-white"
                >
                  <h1
                    className="mb-4 text-center font-bold tracking-wide"
                    style={{
                      fontFamily:
                        "'Space Grotesk', Helvetica, Arial, Lucida, sans-serif",
                      fontSize: "clamp(1rem,1.3vw,2rem)",
                    }}
                  >
                    {terminalData?.cityHeader || t("homePageHeading")}
                  </h1>
                </div>
              </div>
            </div>
          </div>
        </div>

        {isLoading ? (
          <TerminalViewSkeleton />
        ) : (
          <>
            {activeAds.length > 0 && isInActivity && (
              <DisplayAdvertisement advertisements={activeAds} onClose={resetInactivity} />
            )}
            <div
              className="absolute inset-0 -z-10 bg-cover bg-no-repeat opacity-5 min-h-screen"
              style={{
                backgroundImage: `url(${
                  terminalData?.background_url || TerminalDefaultBackground
                  //  || CITYDEFAULTIMAGE
                })`,
              }}
            />
            <div
              className={`flex flex-col ${
                terminalData?.orientation === "horizontal"
                  ? "lg:flex-row"
                  : "flex-col"
              }`}
            >
              <div
                className={`mt-0 flex flex-row justify-center ${
                  terminalData?.orientation === "horizontal" &&
                  (terminalData?.qrCodes?.length > 0 ||
                    terminalData?.services?.length > 0)
                    ? "lg:w-1/2"
                    : "w-full"
                }`}
              >
                {terminalData?.categories &&
                  terminalData?.categories?.length > 0 && (
                    <div className="relative w-[100%] mt-8">
                      {leftSideCategoryList.map((category, index) => {
                        if (category?.listings?.length === 0) return null;

                        // Check if we should use wrap layout (only one category + horizontal orientation)
                        const categoriesWithListings =
                          terminalData.categories.filter(
                            (cat) => cat?.listings?.length > 0
                          );
                        const isOnlyCategory =
                          categoriesWithListings.length === 1;
                        const isWrapLayout =
                          isOnlyCategory &&
                          terminalData?.orientation === "horizontal";

                        return (
                          <ListingCompo
                            name={category?.name}
                            listings={category?.listings}
                            key={index}
                            terminalView={terminalView}
                            setListingPopup={setListingPopup}
                            isWrapLayout={isWrapLayout}
                            isOnlyCategory={isOnlyCategory}
                            isExpanded={isExpanded}
                            handleToggleExpanded={handleToggleExpanded}
                            terminalData={terminalData}
                            index={index}
                          />
                        );
                      })}
                    </div>
                  )}
              </div>

              {terminalData?.orientation === "horizontal" &&
                (terminalData?.qrCodes?.length > 0 ||
                  terminalData?.services?.length > 0) && (
                  <div className="hidden lg:block flex-1 items-center py-6">
                    <div className="w-1  h-full bg-gray-400"></div>
                  </div>
                )}

              <ServiceViewModal
                isOpen={!!selectedService}
                url={selectedService}
                close={() => setSelectedService("")}
                isMap={isMap}
                services={terminalData?.services}
                terminalData={terminalData}
                poiCategories={poiCategories}

              />

              {/* ELEMENT: Services + QR Codes === Horizontal */}
              {terminalData?.orientation === "horizontal" && (
                <div className="lg:w-[48%] flex flex-col">
                  {!!rightSideCategoryList.length && (
                    <div className="relative w-[100%] mt-8">
                      {rightSideCategoryList.map((category, index) => {
                        if (category?.listings?.length === 0) return null;

                        // Check if we should use wrap layout (only one category + horizontal orientation)
                        const categoriesWithListings =
                          terminalData.categories.filter(
                            (cat) => cat?.listings?.length > 0
                          );
                        const isOnlyCategory =
                          categoriesWithListings.length === 1;
                        const isWrapLayout =
                          isOnlyCategory &&
                          terminalData?.orientation === "horizontal";

                        return (
                          <ListingCompo
                            name={category?.name}
                            listings={category?.listings}
                            key={index}
                            terminalView={terminalView}
                            setListingPopup={setListingPopup}
                            isWrapLayout={isWrapLayout}
                            isOnlyCategory={isOnlyCategory}
                            isExpanded={isExpanded}
                            handleToggleExpanded={handleToggleExpanded}
                            terminalData={terminalData}
                            index={index}
                          />
                        );
                      })}
                    </div>
                  )}
                  {!!terminalData?.services?.length && (
                    <div>
                      <div className="font-bold text-2xl 3xl:text-[4rem] my-8 px-8 dark:text-white">
                        {terminalData?.serviceLabel || t("servicesLabel")}
                      </div>
                      <ServicesHorizontal
                        services={terminalData?.services}
                        setSelectedService={setSelectedService}
                        serviceLabel={terminalData?.serviceLabel}
                        setIsMap={setIsMap}
                      />
                    </div>
                  )}
                  {!!terminalData?.qrCodes?.length && (
                    <div>
                      <div className="font-bold text-2xl 3xl:text-[4rem] my-8 px-8">
                        {terminalData?.qrCodeLabel || "QR Codes"}
                      </div>

                      <QrCodes
                        qrCodes={terminalData?.qrCodes}
                        qrCodeLabel={terminalData?.qrCodeLabel}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* ELEMENT: Services + QR Codes === Vertical */}
              {terminalData?.orientation === "vertical" && (
                <>
                  {/* Services */}
                  <VerticalServices
                    setSelectedService={setSelectedService}
                    terminalData={terminalData}
                     setIsMap={setIsMap}
                  />
                  {/* QR Codes */}
                  <VerticalQrCodes terminalData={terminalData} />
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Arrow Icons at Bottom Right */}
      {isScrollableB && (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col space-y-2">
          <button
            className="bg-gray-800 hover:bg-gray-700 text-white p-3 rounded-full shadow-lg transition-colors duration-200"
            aria-label="Arrow Down"
          >
            <ArrowDown size={24} />
          </button>
        </div>
      )}
    </section>
  );
};

export default HomePageTempleteB;